import { Cart } from "./Cart.js";
import { Config } from "./Config.js";
import { ApiService } from "./ApiService.js";
import { PaymentController, CapacityError } from "./PaymentController.js";

export class WidgetController {

    constructor(widget) {
        this.widget = widget;
        this.bindListeners();
    }

    load(data) {
        this.data = data;
        this.program = data.program;
        this.cart = new Cart(this.widget.getCartSection(), data, this.onApplyCode.bind(this), this.onRemoveCode.bind(this));
        if (this.data.schedules.length == 1) {
            this.getWidget().getSingleDaySection().classList.remove("hidden");
        }
    }

    async applyDiscount(promoCode) {
        const response = await ApiService.getDiscount(this.data.eventId, promoCode);

        if (response.ok) {
            const discount = await response.json();
            this.cart.setDiscount(discount);
        } else {
            this.cart.setDiscount(null, `Discount code ${promoCode} not found.`);
        }
    }

    removeDiscount() {
        this.cart.setDiscount(null);
    }

    getWidget() {
        return this.widget;
    }

    bindListeners() {
        this.getWidget().bindCheckoutButton(this.onCheckoutIntent.bind(this));
        this.getWidget().bindQuantityChangeListener(this.onQuantityAdd.bind(this), this.onQuantityRemove.bind(this));
        this.getWidget().bindLocationDetailsListener(this.onLocationDetailsClick.bind(this));
        this.getWidget().bindDaySelectListener(this.onDaySelected.bind(this));
        this.getWidget().bindBuildPassListener(this.onBuildPassSelected.bind(this));
        this.getWidget().bindConfigureDayStationListener(this.onConfigureDayStationSelected.bind(this));
        this.getWidget().bindConfigureDayShuttleListener(this.onConfigureDayShuttleSelected.bind(this));
        this.getWidget().bindConfigureDayEditButtonListener(this.onConfigureDayEdit.bind(this));
        this.getWidget().bindConfigureDayCancelEditButtonListener(this.onConfigureDayCancelEdit.bind(this));
        this.getWidget().bindBackButton(this.onBackToCart.bind(this));
        this.getWidget().bindCloseButton(this.onClose.bind(this));
    }

    async onApplyCode(promoCode) {
        await this.applyDiscount(promoCode);
    }

    onRemoveCode() {
        this.removeDiscount();
    }

    onClose() {
        this.getWidget().closeCart();
    }

    onConfigureDayEdit(event) {
        const scheduleId = event.target.dataset.scheduleId;
        const editButton = this.getWidget().getConfigurePassEditButton(scheduleId);
        const cancelButton = this.getWidget().getConfigurePassCancelEditButton(scheduleId);
        const summarySection = this.getWidget().getConfigurePassSummarySection(scheduleId);
        const configurationSection = this.getWidget().getConfigurePassConfigurationSection(scheduleId);

        summarySection.classList.add("hidden");
        configurationSection.classList.remove("hidden");
        editButton.classList.add("hidden");
        cancelButton.classList.remove("hidden");
        this.getWidget().updateFulldayControls();
    }

    onConfigureDayCancelEdit(event) {
        const scheduleId = event.target.dataset.scheduleId;
        const editButton = this.getWidget().getConfigurePassEditButton(scheduleId);
        const cancelButton = this.getWidget().getConfigurePassCancelEditButton(scheduleId);
        const summarySection = this.getWidget().getConfigurePassSummarySection(scheduleId);
        const configurationSection = this.getWidget().getConfigurePassConfigurationSection(scheduleId);

        summarySection.classList.remove("hidden");
        configurationSection.classList.add("hidden");
        cancelButton.classList.add("hidden");
        editButton.classList.remove("hidden");
        this.getWidget().updateFulldayControls();
    }

    onCheckoutIntent() {
        this.getWidget().setCheckoutButtonLoading(true);
        const cartContents = this.getCartContents();
        const rootElement = this.getWidget().paymentSection();
        const onOrderCreated = () => {
            this.getWidget().showCheckout();
            this.getWidget().setCheckoutButtonLoading(false);
        }
        const onFail = (error) => {
            // A capacity shortfall is the customer's to fix (lower the
            // quantity), so tell them exactly how many seats are left rather
            // than showing the generic "unavailable" message.
            if (error instanceof CapacityError) {
                return this.getWidget().setCheckoutButtonLoading(false, this.capacityMessage(error.shortages));
            }
            const block = Config.handleError(cartContents.cart.eventId);
            if (block === true) {
                return this.getWidget().setCheckoutButtonLoading(false);
            }
            this.getWidget().setCheckoutButtonLoading(false, "Currently unavailable. Please check back later or contact us as info@mride.com.");
        }
        const onOrderComplete = this.onCheckoutComplete.bind(this);
        PaymentController.createCheckout(cartContents, rootElement, onOrderComplete).then(onOrderCreated).catch(onFail);
    }

    /**
     * Customer-facing wording for a capacity shortfall. Names how many seats
     * are left, per shuttle, so the customer knows what quantity will go
     * through. Falls back to a plain message when the detail is missing.
     */
    capacityMessage(shortages = []) {
        if (!Array.isArray(shortages) || shortages.length === 0) {
            return "Some seats sold out while you were booking. Please lower the quantity and try again.";
        }
        const seatWord = (n) => (n === 1 ? "seat" : "seats");
        if (shortages.length === 1) {
            const only = shortages[0];
            const where = only.location || only.shuttleName;
            const remaining = only.seatsRemaining;
            if (remaining === 0) {
                return where ? `${where} is sold out.` : "That departure is sold out.";
            }
            return where
                ? `There are only ${remaining} ${seatWord(remaining)} left for ${where}.`
                : `There are only ${remaining} ${seatWord(remaining)} left.`;
        }
        // More than one shuttle came up short — list each.
        const parts = shortages.map((shortage) => {
            const where = shortage.location || shortage.shuttleName || "one departure";
            return (shortage.seatsRemaining === 0)
                ? `${where} is sold out`
                : `${where} has only ${shortage.seatsRemaining} ${seatWord(shortage.seatsRemaining)} left`;
        });
        return `Not enough seats: ${parts.join("; ")}. Please lower the quantity and try again.`;
    }

    onLocationDetailsClick(locationId) {
        this.getWidget().showLocation(locationId);
    }

    onBackToCart() {
        this.getWidget().hideCheckout();
        this.getWidget().hideLocation();
        this.getWidget().showCart();
    }

    onCheckoutComplete() {
        this.getWidget().hideBackButtons();
    }

    onBuildPassSelected() {
        this.getWidget().getConfigurePassSection().classList.remove("hidden");
    }

    onDaySelected(event) {
        const scheduleId = event.target.value;
        const currentShuttleSections = this.getWidget().getCurrentShuttleSections();

        this.getWidget().getSingleDaySection().classList.add("hidden");

        for (const shuttleSection of currentShuttleSections) {
            shuttleSection.classList.add("hidden");
        }
        const shuttleSections = this.getWidget().getShuttleSections(scheduleId);
        this.getWidget().getSingleDaySection().classList.remove("hidden");
        for (const shuttleSection of shuttleSections) {
            shuttleSection.classList.remove("hidden");
        }
    }

    onConfigureDayStationSelected(event) {
        const scheduleId = event.target.name;
        const stationId = event.target.value;
        const shuttleInputForDay = this.getWidget().getConfigurePassShuttleInputs(scheduleId);
        const shuttleLabelsForDay = this.getWidget().getConfigurePassShuttleLabels(scheduleId);
        const shuttleInputsForStation = this.getWidget().getConfigurePassShuttleStationInputs(stationId);
        const shuttleLabelsForStation = this.getWidget().getConfigurePassShuttleStationLabels(stationId);
        const instructions = this.getWidget().getConfigurePassStationDepartureLabel(scheduleId);
        instructions?.classList.remove("hidden");

        for (const input of shuttleInputForDay) {
            input.classList.add("hidden");
        }
        for (const label of shuttleLabelsForDay) {
            label.classList.add("hidden");
        }
        for (const input of shuttleInputsForStation) {
            input.classList.remove("hidden")
        }
        for (const label of shuttleLabelsForStation) {
            label.classList.remove("hidden")
        }
    }

    onConfigureDayShuttleSelected(event) {
        const scheduleId = event.target.dataset.scheduleId;

        const editButton = this.getWidget().getConfigurePassEditButton(scheduleId);
        const cancelButton = this.getWidget().getConfigurePassCancelEditButton(scheduleId);
        const summarySection = this.getWidget().getConfigurePassSummarySection(scheduleId);
        const configurationSection = this.getWidget().getConfigurePassConfigurationSection(scheduleId);

        const stationName = this.getWidget().getConfigurePassStationName(scheduleId);
        const shuttleTime = this.getWidget().getConfigurePassShuttleName(scheduleId);
        const summaryText = `Depart from ${stationName} at ${shuttleTime}`

        this.getWidget().setConfigurePassSummaryMessage(scheduleId, summaryText);
        summarySection.classList.remove("hidden");
        configurationSection.classList.add("hidden");
        editButton.classList.remove("hidden");
        cancelButton.classList.add("hidden");

        this.getWidget().updateFulldayControls();
        this.getWidget().setPassConfiguration();
        this.cart.updatePassConfiguration(this.getWidget().getFullPassConfiguration());
    }

    getCartContents() {
        const tickets = [];
        const passes = [];
        const ticketData = this.cart.getTickets();
        const passData = this.cart.getPasses();

        for (const shuttleId of Object.keys(ticketData)) {
            const quantity = ticketData[shuttleId].qty;
            tickets.push({shuttleId: shuttleId, quantity: quantity});
        }

        if (passData.qty > 0) {
            passes.push({quantity: passData.qty, shuttles: passData.shuttles.map((shuttle) => shuttle.id)});
        }

        const cartData = {
            cart: { eventId: this.data.eventId, seats: tickets, passes: passes, program: this.program }
        };

        if (this.cart.getPromoCode() != null) {
            cartData.cart.promoCode = this.cart.getPromoCode();
        }

        return cartData;
    }

    onLocationDetailsClick(event) {
        const venueId = event.target.dataset.venueId;
        this.getWidget().showLocation(venueId);
    }

    onQuantityAdd(event) {
        const lineItem = event.target.closest(".lineitem");
        const shuttleId = lineItem.dataset.shuttle;
        let endingQuantity = 0;

        if (shuttleId === "full-pass") {
            const shuttleIds = lineItem.dataset.pass.split(",");
            this.cart.addPass(shuttleIds);
            endingQuantity = this.cart.getPasses().qty;
        } else {
            this.cart.addTicket(shuttleId);
            endingQuantity = this.cart.getTicket(shuttleId).qty;
        }
        this.getWidget().setLineItemQuantity(shuttleId, endingQuantity);
    }

    onQuantityRemove(event) {
        const lineItem = event.target.closest(".lineitem");
        const shuttleId = lineItem.dataset.shuttle;
        let endingQuantity = 0;

        if (shuttleId === "full-pass") {
            this.cart.removePass();
            endingQuantity = this.cart.getPasses().qty;
        } else {
            const cartLineItem = this.cart.getTicket(shuttleId);
            if (cartLineItem) {
                this.cart.removeTicket(shuttleId);
                endingQuantity = this.cart.getTicket(shuttleId)?.qty || 0;
            }
        }
        this.getWidget().setLineItemQuantity(shuttleId, endingQuantity);
    }
}
